/**
 * UI preview helpers for Staff Pricing.
 *
 * Mirrors BillCalculator.included_threshold / PRICING.md commercial maths.
 * Deterministic read-side convenience only — never use as billing authority;
 * persisted truth stays on PricingService / staff pricing APIs.
 * Do not invent alternate commercial formulae.
 */

export type PricingRates = {
  subscriptionFeeUsd: number;
  subscriptionDiscountPct: number;
  includedDiscountPct: number;
  usageDiscountPct: number;
  profitMarginPct: number;
};

export type PricingDiffLine = {
  label: string;
  before: string;
  after: string;
  note?: string;
};

/** subscription_fee / (1 - included_discount%) — same as BillCalculator. */
export function effectiveIncluded(
  fee: number,
  includedDiscountPct: number,
): number {
  const f = Number(fee);
  const disc = Number(includedDiscountPct) / 100;
  if (!Number.isFinite(f) || f < 0) return 0;
  if (!Number.isFinite(disc) || disc >= 1) return 0;
  return f / (1 - disc);
}

export function formatMoneyPreview(n: number | undefined | null): string {
  if (n == null || Number.isNaN(n)) return '—';
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

export function formatPctPreview(n: number | undefined | null): string {
  if (n == null || Number.isNaN(n)) return '—';
  return `${n}%`;
}

/** Field-by-field previous → new summary for confirm modals (A8 human gate). */
export function formatPricingDiff(
  before: PricingRates,
  after: PricingRates,
): PricingDiffLine[] {
  const afterIncluded = effectiveIncluded(
    after.subscriptionFeeUsd,
    after.includedDiscountPct,
  );
  return [
    {
      label: 'Fee',
      before: formatMoneyPreview(before.subscriptionFeeUsd),
      after: formatMoneyPreview(after.subscriptionFeeUsd),
    },
    {
      label: 'Sub discount',
      before: formatPctPreview(before.subscriptionDiscountPct),
      after: formatPctPreview(after.subscriptionDiscountPct),
    },
    {
      label: 'Included discount',
      before: formatPctPreview(before.includedDiscountPct),
      after: formatPctPreview(after.includedDiscountPct),
      note: `included ${formatMoneyPreview(afterIncluded)}`,
    },
    {
      label: 'Usage discount',
      before: formatPctPreview(before.usageDiscountPct),
      after: formatPctPreview(after.usageDiscountPct),
    },
    {
      label: 'Margin',
      before: formatPctPreview(before.profitMarginPct),
      after: formatPctPreview(after.profitMarginPct),
    },
  ];
}

export function truncateOrgId(orgId: string, keep = 8): string {
  if (!orgId || orgId.length <= keep + 1) return orgId;
  return `${orgId.slice(0, keep)}…`;
}

export function ratesFromPartial(
  values: Partial<Record<keyof PricingRates, string | number>>,
): PricingRates {
  return {
    subscriptionFeeUsd: Number(values.subscriptionFeeUsd) || 0,
    subscriptionDiscountPct: Number(values.subscriptionDiscountPct) || 0,
    includedDiscountPct: Number(values.includedDiscountPct) || 0,
    usageDiscountPct: Number(values.usageDiscountPct) || 0,
    profitMarginPct: Number(values.profitMarginPct) || 0,
  };
}
